import React from 'react';
import PropTypes from 'prop-types';
import MediaQuery from 'react-responsive';
import Moment from 'moment';


import { conformToMask } from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import Currency from '../Currency';
import Tooltip from '../Tooltip';
import CancelPendingItem from './CancelPendingItem';
import arrowUpImg from '../../assets/images/arrow-up.png';
import arrowDownImg from '../../assets/images/arrow-down.png';

import CancellationBtn from './CancellationBtn';

import { LAYOUT, MIN_WIDTH } from '../Layout/enums';

import './styles.css';
import { transactionTypes } from './lookups';

const getCurrency = amount => (
  amount ? conformToMask(amount.toFixed(2), createNumberMask({
    prefix: '$',
    allowDecimal: true,
    requireDecimal: true,
    allowNegative: true,
  })).conformedValue : null
);

const getPercentage = amount => (
  amount ? `${amount.toLocaleString('en-US', {
    style: 'decimal',
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%` : '-'
);

const getAmount = amount => (
  amount ? `${amount.toLocaleString('en-US', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}` : '-'
);

const HistoryItemDetail = ({
  label,
  value,
  note,
}) => (
  (label && value && value !== '-') ?
    <div styleName="histItemDetail">
      <div styleName="histItemLabel">
        {
          note ?
            <div styleName="histLabelWithTooltip">{label}: <Tooltip text={note} /></div> :
            <div>{label}:</div>
        }
      </div>
      <div styleName="histItemValue">{value}</div>
    </div> : null
);

const getPrefixBasedOnStatuses = (item) => {
  if (item.transactionType === transactionTypes.MONEY_TRANSACTION && !item.pending) return '';
  if (item.pending) return 'Pending';
  if (item.completed) return 'Completed';
  if (item.canceled) return 'Cancelled';
  return '';
};

const getPostfixBasedOnStatuses = (item) => {
  if (item.transactionType === transactionTypes.MONEY_TRANSACTION && item.canceled) return 'Cancelled';
  if (item.transactionType === transactionTypes.MONEY_TRANSACTION && item.details && !item.pending) {
    return item.details.status;
  }
  return '';
};

const OfferTransactionDetails = (props) => {
  const {
    industry,
    requestedShareQty,
    pricePerShare,
    requestedTotalCurrencyAmt,
    nextClosingDate,
    isConditional,
    minSharePrice,
    maxSharePrice,
  } = props;
  // const calculatedPricePerShare = pricePerShare || (Big(requestedTotalCurrencyAmt) / Big(requestedShareQty));
  return (
    <div styleName="histBodyContentGrid">
      <HistoryItemDetail label="Annual Yield" value={getPercentage(props.yield)} />
      <HistoryItemDetail label="Industry" value={industry} />
      { isConditional ? <HistoryItemDetail label="Price Range" value={`${getCurrency(minSharePrice)} - ${getCurrency(maxSharePrice)}`} /> : null }
      { requestedShareQty && !isConditional ? <HistoryItemDetail label="Share Quantity" value={getAmount(requestedShareQty)} /> : null }
      {/* { calculatedPricePerShare ? <HistoryItemDetail label="Share Quantity:" value={calculatedPricePerShare} /> : null } */}
      { requestedTotalCurrencyAmt ? <HistoryItemDetail label="Total Pending Investment" value={getCurrency(requestedTotalCurrencyAmt)} /> : null }
      { !isConditional ? <HistoryItemDetail label="Share Price" value={getCurrency(pricePerShare)} /> : null }
      <HistoryItemDetail label="Next Closing Date" value={new Moment(nextClosingDate).format('MMM D, YYYY')} />
    </div>
  );
};

const MoneyTransactionDetails = (props) => {
  const {
    date,
    amount,
    status,
    transactionType,
    source,
    target,
  } = props;
  return (
    <div styleName="histBodyContentGrid">
      <HistoryItemDetail label="Date" value={date} />
      <HistoryItemDetail label="Amount" value={getCurrency(amount)} />
      <HistoryItemDetail label="Transaction Type" value={transactionType} />
      <HistoryItemDetail label="Status" value={status} />
      <HistoryItemDetail label="Source Account" value={source} />
      <HistoryItemDetail label="Target Account" value={target} />
    </div>
  );
};

const SecurityTradeDetails = (props) => {
  const {
    transactionType,
    orderType,
    sharesQuantity,
    limitPrice,
    stopPrice,
    netTotal,
    serviceFee,
    expirationTerm,
  } = props;
  return (
    <div styleName="histBodyContentGrid">
      <HistoryItemDetail label="Transaction Type" value={transactionType} />
      <HistoryItemDetail label="Order Type" value={orderType} />
      <HistoryItemDetail label="Share Quantity" value={getAmount(sharesQuantity)} />
      <HistoryItemDetail label="Limit Price" value={getCurrency(limitPrice)} />
      <HistoryItemDetail label="Stop Price" value={getCurrency(stopPrice)} />
      <HistoryItemDetail label="Estimated Gross Total" value={getCurrency(netTotal)} />
      <HistoryItemDetail label="Service Fee" value={getCurrency(serviceFee)} note="For publicly traded securities there is a $10 trade fee charged should you buy shares in the open market (outside of an offering) or sell shares in the future, plus we pass along regulatory fees such as SEC and TAF fees as part of the “Commission/Service Fees”. The SEC and TAF fees or “Service Fees” are very minimal charges." />
      <HistoryItemDetail label="Expiration Term" value={expirationTerm} />
    </div>
  );
};

const getComponent = layout => (props) => {
  const {
    name,
    description,
    notes,
    amount,
    date,
    pending,
    canceled,
    completed,
    cancellationRestricted,
    oid,
    onCancel,
    onExpandToggle,
    isExpanded,
    transactionType,
    details,
  } = props;
  let styleSuff = '';
  const prefix = getPrefixBasedOnStatuses(props);
  const postfix = getPostfixBasedOnStatuses(props);
  switch (layout) {
    case LAYOUT.TAB:
      styleSuff = 'Tab';
      break;
    case LAYOUT.MOB:
      styleSuff = 'Mob';
      break;
    // no default
  }

  const DetailsAction = () => (
    details ?
      <button onClick={onExpandToggle} styleName="histExpandBtn">
        <input
          type="image"
          styleName="expandImg"
          src={isExpanded ? arrowUpImg : arrowDownImg}
          alt="expandImg"
        />
      </button> : <div styleName="histExpandBtn" />
  );

  const Details = () => (
    <div>
      {
      isExpanded ?
        <div styleName="histItemBody">
          <div styleName="histBodyHorizontalLine" />
          <div styleName="histBodyContent">
            <div>
              { transactionType === transactionTypes.OFFER_TRANSACTION ? <OfferTransactionDetails {...props.details} /> : null }
              { transactionType === transactionTypes.MONEY_TRANSACTION ? <MoneyTransactionDetails {...props.details} /> : null }
              { transactionType === transactionTypes.SECURITY_TRADE ? <SecurityTradeDetails {...props.details} /> : null }
            </div>
            {
              pending && !canceled && !completed && !cancellationRestricted ?
                <div styleName="buttonBottomRow">
                  <CancellationBtn
                    transactionType={transactionType}
                    onCancel={onCancel}
                    amount={amount}
                    oid={oid}
                  />
                </div> : null
            }
          </div>
        </div> : null
    }
    </div>
  );
  if (layout === LAYOUT.TAB || layout === LAYOUT.MOB) {
    styleSuff = 'M';
    return (
      <div styleName="histItemContainer">
        <div styleName={`histItemHeader ${isExpanded ? 'histItemHeaderNoBottomBorder' : ''}`}>
          <div styleName="histItemM">
            <div styleName="histRowM">
              <div styleName="descRowM">
                <div styleName={`histName${styleSuff}`}>{`${prefix || ''} ${name} ${postfix ? ` - ${postfix}` : ''}`}</div>
                {notes !== '' && !canceled ?
                  <div styleName={`histNote${styleSuff}`}>
                    <Tooltip text={notes} />
                  </div> : null}
              </div>
              {description !== '' ? <div styleName={`histDesc${styleSuff}`}>{`${description}`}</div> : null}
              <div styleName={`histDate${styleSuff}`}>{date}</div>
            </div>
            <div styleName="dateRowM">
              <Currency styleName={`histAmount${styleSuff}`} amount={amount} />
              {pending && !isExpanded && !completed && !cancellationRestricted &&
                <CancelPendingItem
                  transactionType={transactionType}
                  oid={oid}
                  amount={amount}
                  onCancel={onCancel}
                />
              }
            </div>
          </div>
          <DetailsAction />
        </div>
        <Details />
      </div>
    );
  }

  return (
    <div styleName="histItemContainer">
      <div styleName={`histItemHeader ${isExpanded ? 'histItemHeaderNoBottomBorder' : ''}`}>
        <div styleName={`histItem${styleSuff}`}>
          <div styleName="histRow">
            <div styleName={`histName${styleSuff}`}>{`${prefix || ''} ${name}${description ? ' -' : ''}${postfix ? ` - ${postfix}` : ''}`}&nbsp;</div>
            {description !== '' ? <div styleName={`histDesc${styleSuff}`}>{`${description}`}</div> : null}
            {notes !== '' && !canceled ?
              <div styleName={`histNote${styleSuff}`}>
                <Tooltip text={notes} />
              </div> : null}
            <Currency styleName={`histAmount${styleSuff}`} amount={amount} />
          </div>
          <div styleName="dateRow">
            <div styleName={`histDate${styleSuff}`}>{date}</div>
            {pending && !isExpanded && !completed && !cancellationRestricted &&
              <CancelPendingItem
                transactionType={transactionType}
                oid={oid}
                amount={amount}
                onCancel={onCancel}
              />
            }
          </div>
        </div>
        <DetailsAction />
      </div>
      <Details />
    </div>
  );
};

export const Desktop = getComponent(LAYOUT.DESK);
export const Tablet = getComponent(LAYOUT.TAB);
export const Mobile = getComponent(LAYOUT.MOB);

class HistoryItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      canceled: false,
      expanded: false,
    };

    this.onCancel = this.onCancel.bind(this);
    this.onExpandToggle = this.onExpandToggle.bind(this);
  }

  async onCancel() {
    const { oid, transactionType, amount } = this.props;
    await this.props.onCancel(oid, transactionType, amount);
  }

  onExpandToggle() {
    this.setState({ expanded: !this.state.expanded });
  }

  render() {
    if (this.state.canceled) {
      return <div />;
    }

    const propsToPass = {
      ...this.props,
      onCancel: this.onCancel,
      onExpandToggle: this.onExpandToggle,
      isExpanded: this.state.expanded,
    };
    return (
      <div>
        <MediaQuery
          minWidth={MIN_WIDTH.DESK}
        >
          <Desktop
            {...propsToPass}
          />
        </MediaQuery>
        <MediaQuery
          minWidth={MIN_WIDTH.TAB}
          maxWidth={MIN_WIDTH.DESK - 1}
        >
          <Tablet
            {...propsToPass}
          />
        </MediaQuery>
        <MediaQuery
          maxWidth={MIN_WIDTH.TAB - 1}
        >
          <Mobile
            {...propsToPass}
          />
        </MediaQuery>
      </div>
    );
  }
}


HistoryItem.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  notes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  amount: PropTypes.number,
  date: PropTypes.string,
};

HistoryItem.defaultProps = {
  name: '',
  description: '',
  notes: '',
  amount: null,
  date: '',
};

export default HistoryItem;



// WEBPACK FOOTER //
// ./src/components/History/HistoryItem.js